// Capability-tier device detection for assessment proctoring.
//
// Apple is NOT one platform. macOS Safari, iPad Safari and iPhone Safari have
// very different capabilities, and links opened inside other apps (Gmail,
// LinkedIn, WhatsApp, Instagram) run in a WKWebView that CANNOT access the
// camera/mic at all. Historically the app hard-blocked everything that matched
// /iPad|iPhone|iPod/ with an "iOS Device Not Supported" screen. This module
// replaces that binary block with a capability tier so we can:
//
//   FULL     -> desktop Chrome/Edge/Firefox, macOS Safari   (true fullscreen lockdown)
//   STANDARD -> iPad Safari (iPadOS 16.4+ supports element fullscreen)
//   LITE     -> iPhone Safari (Apple blocks element fullscreen for web apps;
//               proctoring runs in soft mode: snapshots + visibility, no fullscreen gate)
//   BLOCKED  -> in-app WKWebView browsers where getUserMedia is unavailable
//               (must be redirected to open in Safari)
//
// Detection is capability-first (feature probes) with userAgent only used where
// a capability cannot be probed (iPhone vs iPad disambiguation, in-app browsers).
// References: Apple Developer Forums (fullscreen unavailable on iPhone),
// caniuse Fullscreen API, WebKit bug 208667 (getUserMedia not in WKWebView).

export const DEVICE_TIER = {
  FULL: 'FULL',
  STANDARD: 'STANDARD',
  LITE: 'LITE',
  BLOCKED: 'BLOCKED',
};

const ua = () =>
  (typeof navigator !== 'undefined' && navigator.userAgent) || '';

// iPad on iPadOS 13+ reports as desktop "MacIntel" but exposes touch points.
// A genuine Mac reports maxTouchPoints === 0.
export const isIPad = () => {
  if (typeof navigator === 'undefined') return false;
  const touchMac =
    navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1;
  return /iPad/.test(ua()) || touchMac;
};

export const isIPhone = () => /iPhone|iPod/.test(ua());

// Any iOS/iPadOS device (all of which use WebKit under the hood).
export const isIOS = () => isIPhone() || isIPad();

// Mobile operating systems do not expose why a page was backgrounded. An
// incoming call, notification overlay and an intentional app switch all emit
// the same visibility event. Keep those OS interruptions out of the punitive
// warning/auto-submit path; they remain observable to report-only proctoring.
export const isMobileDevice = () =>
  isIOS() || /Android|webOS|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(ua());

let lastMobileInterruptionAt = 0;

export const markMobileInterruption = () => {
  if (!isMobileDevice()) return false;
  lastMobileInterruptionAt = Date.now();
  return true;
};

// Mobile browsers commonly dispatch fullscreenchange immediately after
// visibilitychange when the phone UI takes over. Suppress that companion event
// so a single call cannot be counted as a fullscreen violation.
export const isMobileInterruptionFullscreenExit = (graceMs = 3000) =>
  isMobileDevice() &&
  ((typeof document !== 'undefined' &&
    (document.visibilityState === 'hidden' ||
      (typeof document.hasFocus === 'function' && !document.hasFocus()))) ||
    Date.now() - lastMobileInterruptionAt <= graceMs);

export const isMacSafari = () => {
  const u = ua();
  const isMac = /Macintosh/.test(u) && !isIPad();
  const isSafari = /Safari/.test(u) && !/Chrome|Chromium|Edg|OPR|CriOS|FxiOS/.test(u);
  return isMac && isSafari;
};

// In-app browsers (WKWebView) embedded inside native apps. getUserMedia is
// blocked here on iOS, so the camera/mic checks will silently fail unless we
// detect this and ask the user to open the link in Safari.
//   FBAN/FBAV -> Facebook, Instagram -> Instagram, LinkedInApp -> LinkedIn,
//   GSA -> Google app, Line/Twitter/Snapchat/MicroMessenger -> respective apps.
export const isInAppBrowser = () => {
  const u = ua();
  if (/(FBAN|FBAV|FB_IAB|Instagram|LinkedInApp|Line\/|Snapchat|MicroMessenger|Twitter|GSA\/)/.test(u)) {
    return true;
  }
  // Real third-party iOS browsers (Chrome, Firefox, Edge, Opera, DuckDuckGo)
  // are full WebKit browsers where getUserMedia works — never treat them as
  // in-app shells. They tag themselves in the UA.
  const isRealIOSBrowser = /(CriOS|FxiOS|EdgiOS|OPiOS|DuckDuckGo)/.test(u);
  if (isRealIOSBrowser) return false;
  // Heuristic for iOS WKWebViews that don't tag themselves: real mobile Safari
  // contains "Version/<n> ... Safari/". A bare WKWebView shell omits "Version/".
  if (isIOS()) {
    const looksLikeSafari = /Safari/.test(u) && /Version\//.test(u);
    const standalone =
      typeof navigator !== 'undefined' && navigator.standalone === true; // Add-to-Home-Screen PWA
    if (!looksLikeSafari && !standalone) return true;
  }
  return false;
};

// True if the browser can put an arbitrary element (a <div>) into fullscreen.
// iPhone Safari exposes NO element fullscreen API (only <video> can fullscreen
// via webkitEnterFullscreen), so this is false on iPhone and true on iPad/desktop.
export const canElementFullscreen = () => {
  if (typeof document === 'undefined') return false;
  const el = document.documentElement;
  const hasApi = !!(
    el.requestFullscreen ||
    el.webkitRequestFullscreen ||
    el.mozRequestFullScreen ||
    el.msRequestFullscreen
  );
  // iPhone Safari historically exposes webkitRequestFullscreen on elements but
  // it is a no-op for non-video elements — treat iPhone as no-fullscreen.
  if (isIPhone()) return false;
  return hasApi;
};

export const hasGetUserMedia = () =>
  typeof navigator !== 'undefined' &&
  !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);

// Resolve the proctoring capability tier for the current device/browser.
export const resolveDeviceTier = () => {
  // In-app browsers can't reach the camera/mic — block before anything else.
  if (isInAppBrowser() || !hasGetUserMedia()) {
    return DEVICE_TIER.BLOCKED;
  }
  if (isIPhone()) {
    return DEVICE_TIER.LITE;
  }
  if (isIPad()) {
    return DEVICE_TIER.STANDARD;
  }
  // Desktop (incl. macOS Safari) and anything else capable.
  return DEVICE_TIER.FULL;
};

// Whether the assessment runtime should enforce the fullscreen lockdown
// (request fullscreen on start, treat fullscreen-exit as a violation).
export const shouldEnforceFullscreen = (tier = resolveDeviceTier()) =>
  tier === DEVICE_TIER.FULL || tier === DEVICE_TIER.STANDARD;

// Convenience descriptor for telemetry / proctoring upload payloads.
export const getDeviceTierInfo = () => {
  const tier = resolveDeviceTier();
  return {
    tier,
    proctoringMode: shouldEnforceFullscreen(tier) ? 'strict' : 'lite',
    isIPhone: isIPhone(),
    isIPad: isIPad(),
    isMacSafari: isMacSafari(),
    canFullscreen: canElementFullscreen(),
  };
};
